import { Responses } from '../../../../../resources/responses';
import Util from '../../../../../resources/util';
import ZoneId from '../../../../../resources/zone_id';
import { RaidbossData } from '../../../../../types/data';
import { TriggerSet } from '../../../../../types/trigger';

export interface Data extends RaidbossData {
  sealed?: boolean;
  side?: string;
  mythcall?: boolean;
  bracelets?: string;
  seenHostile?: boolean;
  clones?: string[];
  blackHoleCount?: number;
}

const triggerSet: TriggerSet<Data> = {
  id: 'TheForbiddenLandEurekaHydatos',
  zoneId: ZoneId.TheForbiddenLandEurekaHydatos,
  comments: {
    en: 'Baldesion Arsenal triggers/timelines only',
    de: 'Nur Baldesion Arsenal Trigger/Timeline ',
    fr: 'Triggers/Timeline pour l\'Arsenal de Baldesion uniquement',
    cn: '只有BA (巴尔德西昂兵武塔) 触发器/时间轴',
    ko: '발데시온 무기고 트리거/타임라인만',
    tc: '只有BA (巴爾德西昂兵武塔) 觸發器/時間軸',
  },
  timelineFile: 'eureka_hydatos.txt',
  resetWhenOutOfCombat: false,
  timelineTriggers: [
    {
      id: 'BA Art Geas',
      regex: /Legendary Geas/,
      beforeSeconds: 0,
      response: Responses.stopMoving(),
    },
    {
      id: 'BA Raiden Levinwhorl',
      regex: /Levinwhorl/,
      beforeSeconds: 10,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Shields and Mitigation',
          de: 'Schilde und Abschwächungen',
          fr: 'Boucliers et mitigation',
          ja: 'ダメージ軽減とバリアを',
          cn: '大AOE，刷盾减伤',
          ko: '뎀감, 보호막',
          tc: '大AOE，刷盾減傷',
        },
      },
    },
    {
      id: 'BA AV Eurekan Potion',
      regex: /Explosive Impulse/,
      beforeSeconds: 10,
      suppressSeconds: 60,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Pop Eurekan Potions',
          de: 'Eureka-Heiltränke benutzen',
          fr: 'Utilisez potion d\'Eurêka',
          ja: 'エウレカ回復薬',
          cn: '磕优雷卡回复药',
          ko: '에우레카 포션 먹기',
          tc: '使用優雷卡回復藥',
        },
      },
    },
    {
      id: 'BA Ozma Black Hole Warning',
      regex: /Black Hole/,
      beforeSeconds: 12,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Black Hole Soon',
          de: 'Schwarzes Loch',
          fr: 'Trou noir bientôt',
          ja: 'まもなくブラックホール',
          cn: '黑洞警告',
          ko: '곧 블랙홀',
          tc: '黑洞警告',
        },
      },
    },
  ],
  triggers: [
    // https://xivapi.com/LogMessage/9069
    // en: The memories of heroes past live on again!
    {
      id: 'BA Saved By Remembrance',
      type: 'ActorControlSelfExtra',
      netRegex: { category: Util.actorControlType.logMsg, param1: '236D', capture: false },
      sound: 'Long',
    },
    {
      id: 'BA Seal',
      type: 'SystemLogMessage',
      // "will be sealed off"
      netRegex: { id: '7DC', capture: false },
      run: (data) => data.sealed = true,
    },
    {
      id: 'BA Clear Data',
      type: 'SystemLogMessage',
      // "is no longer sealed"
      netRegex: { id: '7DE', capture: false },
      run: (data) => {
        delete data.side;
        delete data.mythcall;
        delete data.clones;
        delete data.bracelets;
        delete data.sealed;
        delete data.blackHoleCount;
        delete data.seenHostile;
      },
    },
    {
      id: 'BA West Side',
      type: 'Ability',
      // The two sides are far enough apart that even though you see the autos
      // from the opposite boss, you don't see the player target (which is blank).
      // This considers you on the west side if Art autos somebody, and the name isn't blank.
      netRegex: { id: '3956', source: 'Art', target: '[^:]+', capture: false },
      suppressSeconds: 1000,
      run: (data) => data.side = 'west',
    },
    {
      id: 'BA East Side',
      type: 'Ability',
      netRegex: {
        id: '3957',
        source: 'Owain',
        target: '[^:]+',
        capture: false,
      },
      suppressSeconds: 1000,
      run: (data) => data.side = 'east',
    },
    {
      id: 'BA Art Mythcall',
      type: 'StartsUsing',
      netRegex: { id: '3927', source: 'Art', capture: false },
      condition: (data) => data.side === 'west',
      run: (data) => data.mythcall = true,
    },
    {
      id: 'BA Art Tankbuster',
      type: 'StartsUsing',
      netRegex: { id: '3934', source: 'Art' },
      condition: (data) => data.side === 'west',
      response: Responses.tankBuster(),
    },
    {
      id: 'BA Art Orb Marker',
      type: 'HeadMarker',
      netRegex: { id: '005C' },
      condition: (data) => data.side === 'west',
      alarmText: (data, matches, output) => {
        if (data.me !== matches.target)
          return;
        return output.orbOnYou!();
      },
      alertText: (data, matches, output) => {
        if (data.me === matches.target)
          return;
        return output.awayFromOrbMarker!();
      },
      outputStrings: {
        awayFromOrbMarker: {
          en: 'Away From Orb Marker',
          de: 'Weg vom Orb-Marker',
          fr: 'Éloignez-vous du marquage Orbe',
          ja: '玉から離れる',
          cn: '远离点名',
          ko: '구슬 대상자에서 떨어지기',
          tc: '遠離球點名',
        },
        orbOnYou: {
          en: 'Orb on YOU',
          de: 'Orb auf DIR',
          fr: 'Orbe sur VOUS',
          ja: '自分に玉',
          cn: '点名',
          ko: '구슬 대상자',
          tc: '球點名',
        },
      },
    },
    {
      id: 'BA Art Piercing Dark Marker',
      type: 'HeadMarker',
      netRegex: { id: '008B' },
      condition: (data, matches) => data.side === 'west' && data.me === matches.target,
      response: Responses.spread(),
    },
    {
      id: 'BA Art Legendcarver',
      type: 'StartsUsing',
      netRegex: { id: '3928', source: 'Art', capture: false },
      condition: (data) => data.side === 'west',
      response: Responses.getOut(),
    },
    {
      id: 'BA Art Legendspinner',
      type: 'StartsUsing',
      netRegex: { id: '3929', source: 'Art', capture: false },
      condition: (data) => data.side === 'west',
      response: Responses.getIn(),
    },
    {
      id: 'BA Art Mythcall Legendcarver',
      type: 'StartsUsing',
      netRegex: { id: '3928', source: 'Art', capture: false },
      condition: (data) => data.side === 'west' && data.mythcall,
      delaySeconds: 3.5,
      response: Responses.getUnder(),
    },
    {
      id: 'BA Art Mythcall Legendspinner',
      type: 'StartsUsing',
      netRegex: { id: '3929', source: 'Art', capture: false },
      condition: (data) => data.side === 'west' && data.mythcall,
      delaySeconds: 3.5,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Under Spears',
          de: 'Unter einen Speer',
          fr: 'En dessous des lances',
          ja: '妖槍旋',
          cn: '枪脚下',
          ko: '창 아래로',
          tc: '槍腳下',
        },
      },
    },
    {
      id: 'BA Owain Tankbuster',
      type: 'StartsUsing',
      netRegex: { id: '3945', source: 'Owain' },
      condition: (data) => data.side === 'west',
      response: Responses.tankBuster(),
    },
    {
      id: 'BA Owain Piercing Light Marker',
      type: 'HeadMarker',
      netRegex: { id: '008B' },
      condition: (data, matches) => data.side === 'east' && data.me === matches.target,
      response: Responses.spread(),
    },
    {
      id: 'BA Owain Dorito Stack',
      type: 'HeadMarker',
      netRegex: { id: '008B' },
      condition: (data, matches) => data.side === 'east' && data.me === matches.target,
      response: Responses.doritoStack(),
    },
    // https://xivapi.com/PublicContentTextData/2122
    // en: Munderg, turn flesh to ash!
    {
      id: 'BA Owain Fire Element',
      type: 'ActorControlExtra',
      netRegex: {
        category: Util.actorControlType.publicContentText,
        param2: '84A',
        capture: false,
      },
      condition: (data) => data.side === 'east',
      alertText: (_data, _matches, output) => output.getToIce!(),
      infoText: (_data, _matches, output) => output.switchMagia!(),
      outputStrings: {
        switchMagia: {
          en: 'Switch Magia',
          de: 'Magia-Brett drehen',
          fr: 'Changez de Magia',
          ja: 'マギアボードを切替',
          cn: '切换元素板',
          ko: '마기아 전환',
          tc: '切換元素板',
        },
        getToIce: {
          en: 'Get to Ice',
          de: 'Geh zum Eis',
          fr: 'Allez à la glace',
          ja: '氷に',
          cn: '冰',
          ko: '얼음',
          tc: '冰',
        },
      },
    },
    // https://xivapi.com/PublicContentTextData/2123
    // en: Munderg, turn blood to ice!
    {
      id: 'BA Owain Ice Element',
      type: 'ActorControlExtra',
      netRegex: {
        category: Util.actorControlType.publicContentText,
        param2: '84B',
        capture: false,
      },
      condition: (data) => data.side === 'east',
      alertText: (_data, _matches, output) => output.getToFire!(),
      infoText: (_data, _matches, output) => output.switchMagia!(),
      outputStrings: {
        switchMagia: {
          en: 'Switch Magia',
          de: 'Magia-Brett drehen',
          fr: 'Changez de Magia',
          ja: 'マギアボードを切替',
          cn: '切换元素板',
          ko: '마기아 전환',
          tc: '切換元素板',
        },
        getToFire: {
          en: 'Get to Fire',
          de: 'Geh zum Feuer',
          fr: 'Allez au feu',
          ja: '火',
          cn: '火',
          ko: '불',
          tc: '火',
        },
      },
    },
    {
      id: 'BA Owain Ivory Palm',
      type: 'Ability',
      netRegex: { id: '3941', source: 'Ivory Palm' },
      condition: (data, matches) => data.side === 'east' && data.me === matches.target,
      response: Responses.doritoStack(),
    },
    {
      id: 'BA Owain Pitfall',
      type: 'StartsUsing',
      netRegex: { id: '394D', source: 'Owain', capture: false },
      condition: (data) => data.side === 'east',
      response: Responses.getOut(),
    },
    {
      id: 'BA Silence Centaur',
      type: 'StartsUsing',
      netRegex: { id: '3BFE', source: 'Arsenal Centaur' },
      condition: (data) => data.CanSleep(),
      response: Responses.sleep(),
    },
    {
      id: 'BA Raiden Tankbuster',
      type: 'StartsUsing',
      netRegex: { id: '387B', source: 'Raiden' },
      condition: (data) => data.sealed,
      response: Responses.tankBuster(),
    },
    {
      id: 'BA Raiden Lancing Bolt',
      type: 'HeadMarker',
      netRegex: { id: '008A' },
      condition: (data, matches) => data.sealed && data.me === matches.target,
      response: Responses.spread(),
    },
    {
      id: 'BA Raiden Ame',
      type: 'StartsUsing',
      netRegex: { id: '3868', source: 'Raiden', capture: false },
      condition: (data) => data.sealed,
      response: Responses.getOut(),
    },
    {
      id: 'BA Raiden Whirling',
      type: 'StartsUsing',
      netRegex: { id: '386A', source: 'Raiden', capture: false },
      condition: (data) => data.sealed,
      response: Responses.getUnder(),
    },
    {
      id: 'BA Raiden For Honor',
      type: 'StartsUsing',
      netRegex: { id: '387C', source: 'Raiden', capture: false },
      condition: (data) => data.sealed,
      response: Responses.getOut(),
    },
    {
      id: 'BA Raiden Lateral 1',
      type: 'StartsUsing',
      netRegex: { id: '386C', source: 'Raiden', capture: false },
      condition: (data) => data.sealed,
      response: Responses.goLeft(),
    },
    {
      id: 'BA Raiden Lateral 2',
      type: 'StartsUsing',
      netRegex: { id: '386B', source: 'Raiden', capture: false },
      condition: (data) => data.sealed,
      response: Responses.goRight(),
    },
    {
      id: 'BA AV Tankbuster',
      type: 'StartsUsing',
      netRegex: { id: '379A', source: 'Absolute Virtue' },
      condition: (data) => data.sealed,
      response: Responses.tankBuster(),
    },
    {
      id: 'BA AV Eidos Dark Bracelets',
      type: 'StartsUsing',
      netRegex: { id: '3787', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      infoText: (_data, _matches, output) => output.text!(),
      run: (data) => data.bracelets = 'dark',
      outputStrings: {
        text: {
          en: 'Dark Bracelets',
          de: 'Dunkle Armreife',
          fr: 'Bracelets ténèbreux',
          ja: '黒リング',
          cn: '黑光环',
          ko: '어두운 고리',
          tc: '黑光環',
        },
      },
    },
    {
      id: 'BA AV Eidos Light Bracelets',
      type: 'StartsUsing',
      netRegex: { id: '3786', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      infoText: (_data, _matches, output) => output.text!(),
      run: (data) => data.bracelets = 'light',
      outputStrings: {
        text: {
          en: 'Light Bracelets',
          de: 'Helle Armreife',
          fr: 'Bracelets lumineux',
          ja: '白リング',
          cn: '白光环',
          ko: '빛 고리',
          tc: '白光環',
        },
      },
    },
    {
      id: 'BA AV Eidos Hostile Aspect',
      type: 'StartsUsing',
      netRegex: { id: '378B', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      alertText: (data, _matches, output) => {
        if (!data.seenHostile) {
          if (data.bracelets === 'light')
            return output.awayFromLightCircles!();

          if (data.bracelets === 'dark')
            return output.awayFromDarkCircles!();

          return;
        }
        if (data.bracelets === 'light')
          return output.standByDarkCircles!();

        if (data.bracelets === 'dark')
          return output.standByLightCircles!();
      },
      run: (data) => data.seenHostile = true,
      outputStrings: {
        awayFromLightCircles: {
          en: 'Away From Light Circles',
          de: 'Weg von hellen Kreisen',
          fr: 'Éloignez-vous des cercles lumineux',
          ja: '白リングから離れる',
          cn: '远离白圈',
          ko: '밝은 원에서 떨어지기',
          tc: '遠離白圈',
        },
        awayFromDarkCircles: {
          en: 'Away From Dark Circles',
          de: 'Weg von dunklen Kreisen',
          fr: 'Éloignez-vous des cercles ténèbreux',
          ja: '黒リングから離れる',
          cn: '远离黑圈',
          ko: '어두운 원에서 떨어지기',
          tc: '遠離黑圈',
        },
        standByDarkCircles: {
          en: 'Stand By Dark Circles',
          de: 'Zu den dunklen Kreisen',
          fr: 'Placez-vous près des cercles ténèbreux',
          ja: '黒リングに近づく',
          cn: '靠近黑圈',
          ko: '어두운 원 옆에 서기',
          tc: '靠近黑圈',
        },
        standByLightCircles: {
          en: 'Stand By Light Circles',
          de: 'zu den hellen Kreisen',
          fr: 'Placez-vous près des cercles lumineux',
          ja: '白リングに近づく',
          cn: '靠近白圈',
          ko: '밝은 원 옆에 서기',
          tc: '靠近白圈',
        },
      },
    },
    {
      id: 'BA AV Eidos Impact Stream',
      type: 'StartsUsing',
      netRegex: { id: '3788', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      alertText: (data, _matches, output) => {
        if (data.bracelets === 'light')
          return output.dark!();

        if (data.bracelets === 'dark')
          return output.light!();
      },
      outputStrings: {
        dark: {
          en: 'Dark',
          de: 'Dunkel',
          fr: 'Ténèbres',
          ja: '黒',
          cn: '黑',
          ko: '어둠',
          tc: '黑',
        },
        light: {
          en: 'Light',
          de: 'Hell',
          fr: 'Lumière',
          ja: '白',
          cn: '白',
          ko: '빛',
          tc: '白',
        },
      },
    },
    {
      // Note: These use 00:3...: lines, without any proper "gains effect" lines.
      // In other words, they need to be fully translated in the game log.
      // There are no "gainsEffect" lines for the clones, only for Absolute Virtue directly.
      // Ideally parser logic could be added for this case, but this is where we are.
      // Note: Use .*? in the regex, as it appears from recent logs that special characters
      // may be included in these lines, e.g.:
      // 332E||Relative Virtue gains the effect of Umbral Essence.|
      //
      // TODO: There are ActorControl packets (GainEffect, category=0x0014) for these effects,
      // but the FFXIV parsing plugin does not emit 0x1A lines. This is being looked at.
      // See OverlayPlugin/cactbot#99 for further info.
      id: 'BA AV Eidos Relative Virtue Astral',
      type: 'GameLog',
      netRegex: {
        line: 'Relative Virtue gains the effect of .*?Astral Essence.*?',
        capture: false,
      },
      condition: (data) => data.sealed,
      run: (data) => {
        // RV clones get buffs in the reverse order that they do their attacks in.
        data.clones ??= [];
        data.clones.push('astral');
      },
    },
    {
      // See note above for `BA AV Eidos Relative Virtue Astral`.
      id: 'BA AV Eidos Relative Virtue Umbral',
      type: 'GameLog',
      netRegex: {
        line: 'Relative Virtue gains the effect of .*?Umbral Essence.*?',
        capture: false,
      },
      condition: (data) => data.sealed,
      run: (data) => {
        // RV clones get buffs in the reverse order that they do their attacks in.
        data.clones ??= [];
        data.clones.push('umbral');
      },
    },
    {
      id: 'BA AV Triple Impact Stream',
      type: 'StartsUsing',
      netRegex: { id: '3797', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      alertText: (data, _matches, output) => {
        if (!data.clones)
          return;
        const wrists = data.clones.pop();
        if (wrists === 'Astral')
          return output.dark!();

        if (wrists === 'Umbral')
          return output.light!();
      },
      outputStrings: {
        dark: {
          en: 'Dark',
          de: 'Dunkel',
          fr: 'Ténèbres',
          ja: '黒',
          cn: '黑',
          ko: '어둠',
          tc: '黑',
        },
        light: {
          en: 'Light',
          de: 'Hell',
          fr: 'Lumière',
          ja: '白',
          cn: '白',
          ko: '빛',
          tc: '白',
        },
      },
    },
    {
      id: 'BA AV Eidos Turbulent Aether',
      type: 'Ability',
      netRegex: { id: '3790', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Orbs to Opposite Colors',
          de: 'Kugeln zu umgekehrter Farbe',
          fr: 'Orbes aux couleurs opposées',
          ja: '反対の色に',
          cn: '连线去相反颜色',
          ko: '구슬 반대 색깔로',
          tc: '連線去相反顏色',
        },
      },
    },
    {
      id: 'BA AV Call Wyvern',
      type: 'Ability',
      netRegex: { id: '3798', source: 'Absolute Virtue', capture: false },
      condition: (data) => data.sealed,
      response: Responses.killAdds(),
    },
    {
      id: 'BA Ozma Sphere Form',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: ['37B3', '37A5', '379F'], capture: false },
      condition: (data) => data.sealed,
      preRun: (data) => {
        data.blackHoleCount = (data.blackHoleCount ?? 0) + 1;
      },
      alarmText: (data, _matches, output) => output.blackHole!({ num: data.blackHoleCount }),
      tts: (data, _matches, output) => output.blackHoleTTS!({ num: data.blackHoleCount }),
      outputStrings: {
        blackHole: {
          en: 'Black Hole ${num} / 6',
          de: 'Schwarzes Loch ${num} / 6',
          fr: 'Trou noir ${num} / 6',
          ja: 'ブラックホール: ${num} / 6',
          cn: '黑洞 ${num} / 6',
          ko: '블랙홀${num} / 6',
          tc: '黑洞 ${num} / 6',
        },
        blackHoleTTS: {
          en: 'Black Hole ${num}',
          de: 'Schwarzes Loch ${num}',
          fr: 'Trou noir ${num}',
          ja: 'ブラックホール ${num}',
          cn: '黑洞 ${num}',
          ko: '블랙홀${num}',
          tc: '黑洞 ${num}',
        },
      },
    },
    // FIXME: on all these forms, most of the time they come with double mechanics,
    // so probably need some "Then," text so folks don't leave the first mechanic early.
    // FIXME: random shade mechanic mid ozma form comes with holy/acceleration bomb/knockback
    // so call out something like "get off + holy" or "get knocked in".
    // FIXME: need callouts for knockback, and maybe "holy soon"?
    {
      id: 'BA Ozma Pyramid Form',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '37A4', capture: false },
      condition: (data) => data.sealed,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Off the Platform',
          de: 'Weg von der Fläche',
          fr: 'Hors de la plateforme',
          ja: '回避',
          cn: '远离平台',
          ko: '멀어지기',
          tc: '遠離平台',
        },
      },
    },
    {
      id: 'BA Ozma Pyramid Form 2',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '37A4', capture: false },
      condition: (data) => data.sealed,
      delaySeconds: 9,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Spread for Bleed',
          de: 'Blutung verteilen',
          fr: 'Dispersez-vous pour le saignement',
          ja: '散開',
          cn: '分散',
          ko: '산개',
          tc: '分散',
        },
      },
    },
    {
      id: 'BA Ozma Star Form',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '37B2', capture: false },
      condition: (data) => data.sealed,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go Far',
          de: 'Weit weg',
          fr: 'Éloignez-vous',
          ja: '離れる',
          cn: '远离',
          ko: '멀리가기',
          tc: '遠離',
        },
      },
    },
    {
      id: 'BA Ozma Star Form 2',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '37B2', capture: false },
      condition: (data) => data.sealed,
      delaySeconds: 9,
      infoText: (data, _matches, output) => {
        // FIXME: taking multiple autos probably means tanking,
        // so probably could figure this out automatically.
        if (data.role === 'tank')
          return output.stackIfNotTanking!();

        return output.stackUp!();
      },
      outputStrings: {
        stackIfNotTanking: {
          en: 'Stack (if not tanking)',
          de: 'Stack (wenn nicht am tanken)',
          fr: 'Packez-vous (sauf les tanks)',
          ja: '集合 (MTではないなら)',
          cn: '集合（如果没在坦怪）',
          ko: '집합 (탱킹 중인 사람 제외)',
          tc: '集合（如果沒在坦怪）',
        },
        stackUp: {
          en: 'Stack Up',
          de: 'Stacken',
          fr: 'Packez-vous en haut',
          ja: '集合',
          cn: '集合',
          ko: '집합',
          tc: '集合',
        },
      },
    },
    {
      id: 'BA Ozma Cube Form',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '379E', capture: false },
      condition: (data) => data.sealed,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Get Close',
          de: 'Nah dran',
          fr: 'Rapprochez-vous',
          ja: '近づく',
          cn: '靠近',
          ko: '가까이',
          tc: '靠近',
        },
      },
    },
    {
      id: 'BA Ozma Cube Form 2',
      type: 'Ability',
      netRegex: { source: 'Proto Ozma', id: '379E', capture: false },
      condition: (data) => data.sealed,
      delaySeconds: 9,
      alertText: (data, _matches, output) => {
        // FIXME: taking multiple autos probably means tanking,
        // so probably could figure this out automatically.
        if (data.role === 'tank')
          return output.offtanksGetOrbs!();
      },
      infoText: (data, _matches, output) => {
        if (data.role !== 'tank')
          return output.stackAwayFromTank!();
      },
      outputStrings: {
        stackAwayFromTank: {
          en: 'Stack Away From Tank',
          de: 'Weg vom Tank stacken',
          fr: 'Packez-vous loin du tank',
          ja: 'タンクにから離れて集合',
          cn: '远离坦克集合',
          ko: '탱커에서 멀어지기',
          tc: '遠離坦克集合',
        },
        offtanksGetOrbs: {
          en: 'Offtanks Get Orbs',
          de: 'Offtanks holt Kugeln',
          fr: 'Offtanks, prenez les orbes',
          ja: 'STは玉を処理',
          cn: 'ST撞球',
          ko: '섭탱 구슬 가져가기',
          tc: 'ST撞球',
        },
      },
    },
    {
      id: 'BA Ozma Pyramid Shade',
      type: 'Ability',
      netRegex: { source: ['Ozmashade', 'Shadow'], id: '37A4', capture: false },
      condition: (data) => data.sealed,
      suppressSeconds: 1,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Get Off',
          de: 'Weg da',
          fr: 'Descendez',
          ja: '回避',
          cn: '远离平台',
          ko: '멀어지기',
          tc: '遠離平台',
        },
      },
    },
    {
      id: 'BA Ozma Star Shade',
      type: 'Ability',
      netRegex: { source: ['Ozmashade', 'Shadow'], id: '37B2', capture: false },
      condition: (data) => data.sealed,
      suppressSeconds: 1,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Get Close',
          de: 'Nah dran',
          fr: 'Rapprochez-vous',
          ja: '近づく',
          cn: '靠近',
          ko: '가까이',
          tc: '靠近',
        },
      },
    },
    {
      id: 'BA Ozma Cube Shade',
      type: 'Ability',
      netRegex: { source: ['Ozmashade', 'Shadow'], id: '379E', capture: false },
      condition: (data) => data.sealed,
      suppressSeconds: 1,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Go Far',
          de: 'Weit weg',
          fr: 'Éloignez-vous',
          ja: '離れる',
          cn: '远离',
          ko: '멀리',
          tc: '遠離',
        },
      },
    },
    {
      id: 'BA Ozma Adds',
      type: 'Ability',
      netRegex: { source: 'Arsenal Urolith', id: '37B0', capture: false },
      condition: (data) => data.sealed,
      delaySeconds: 2,
      suppressSeconds: 1,
      response: Responses.killAdds(),
    },
    {
      id: 'BA Ozma Acceleration Bomb',
      type: 'Ability',
      netRegex: { id: '37AA', source: 'Proto Ozma' },
      condition: (data, matches) => data.sealed && data.me === matches.target,
      response: Responses.stopEverything(),
    },
    {
      id: 'BA Ozma Meteor',
      type: 'HeadMarker',
      netRegex: { id: '0039' },
      condition: (data, matches) => data.sealed && data.me === matches.target,
      response: Responses.meteorOnYou(),
    },
  ],
  timelineReplace: [
    {
      'locale': 'de',
      'replaceSync': {
        'Absolute Virtue': 'Absolut(?:e|er|es|en) Tugend',
        'Arsenal Centaur': 'Arsenal-Zentaur',
        'Art': 'Art',
        'Ball Lightning': 'Elektrosphäre',
        'Arsenal Urolith': 'Arsenal-Urolith',
        'Ivory Palm': 'weiß(?:e|er|es|en) Hand',
        'Orlasrach': 'Orlasrach',
        'Owain': 'Owain',
        '(?<! )Ozma(?!\\w)': 'Yadis',
        'Ozmashade': 'Yadis-Schatten',
        'Proto Ozma(?! containment)': 'Proto-Yadis',
        'Raiden': 'Raiden',
        'Relative Virtue(?! gains)': 'Relative Tugend',
        'Shadow': 'Proto-Yadis-Schatten',
        'Streak Lightning': 'Blitzladung',
        '5 minutes have elapsed since your last activity':
          'Seit deiner letzten Aktivität sind 5 Minuten vergangen',
        'The memories of heroes past live on again':
          'Das Vermächtnis vergangener Helden lebt von Neuem auf',
        'Munderg, turn flesh to ash': 'Munderg, entfessele den Flammeneid',
        'Munderg, turn blood to ice': 'Munderg, das Eis der Ewigkeit soll sie für Äonen bannen',
        'The Shin-Zantetsuken Containment Unit': 'Shin-Zantetsuken-Quarantäneblock',
        'The Lance of Virtue Containment Unit': 'Lanze der Tugend-Quarantäneblock',
        'The Proto Ozma Containment Unit': 'Proto-Yadis-Quarantäneblock',
        'Relative Virtue gains the effect of .*?Astral Essence.*?':
          'Die Relative Tugend erhält den Effekt von.*?Arm der Lichts',
        'Relative Virtue gains the effect of .*?Umbral Essence.*?':
          'Die Relative Tugend erhält den Effekt von.*?Arm der Dunkelheit',
      },
      'replaceText': {
        '\\(Stack\\)': '(Sammeln)',
        'Acallam Na Senorach': 'Legendärer Lanzenwirbel',
        'Acceleration Bomb': 'Beschleunigungsbombe',
        'Adds': 'Adds',
        'Ame-no-Sakahoko': 'Himmelsriposte',
        'Auroral Wind': 'Aurorawind',
        'Bitter Barbs': 'Dornige Schuld',
        'Black Hole': 'Schwarzes Loch',
        'Bleed Attack': 'Blutungs-Angriff',
        'Booming Lament': 'Donnerschlag der Trauer',
        'Call Wyvern': 'Wyvernruf',
        'Carver/Spinner': 'Schnitzer/Spinner',
        'Cloud to Ground': 'Sturmkonzentration',
        'Cube Form': 'Würfel Form',
        'Eidos': 'Sarva',
        'Elemental Magicks': 'Elementmagie',
        'Elemental Shift': 'Elementwechsel',
        'Execration': 'Exsekration',
        'Explosive Impulse': 'Explosiver Impuls',
        'Flare Star': 'Flare-Stern',
        'For Honor': 'Hieb der Gefallenen',
        'Holy': 'Sanctus',
        'Hostile Aspect': 'Polarisierte Welle',
        'Impact Stream': 'Durchschlagsstrom',
        'Ivory Palm': 'weiße Hand',
        'Lancing Bolt': 'Donnerlanze',
        'Lateral Zantetsuken': 'Kata-Zantetsuken',
        'Legendary Geas': 'Legendenhieb',
        'Legendary Imbas': 'Legendäre Boshaftigkeit',
        'Legendcarver': 'Legendenschnitzer',
        'Legendspinner': 'Legendenspinner',
        'Levinwhorl': 'Wirbelsturm',
        'Medusa Javelin': 'Medusenspeer',
        'Meteor': 'Meteor',
        'Mourning Star': 'Morgenstern',
        'Mythcall': 'Mythenruf',
        'Orb x5': 'Kugel x5',
        'Ozma Ability': 'Yadis Fähigkeit',
        'Piercing Dark': 'Lanze der Finsternis',
        'Piercing Light': 'Lanze des Lichts',
        'Pitfall': 'Berstender Boden',
        'Pyramid Form': 'Pyramiden Form',
        'Random Form': 'Zufällige Form',
        'Random Shade': 'Zufällige Schatten',
        'Shade Ability': 'Schatten Fähigkeit',
        'Shingan': 'Betäubungsschlag',
        'Shooting Star': 'Sternschnuppe',
        'Soak Attack': 'Angriff absorbieren',
        'Spear Copy': 'Speer kopieren',
        'Spear Shade': 'Speer Schatten',
        'Sphere Form': 'Kugel Form',
        'Spiritcull': 'Kettendämon',
        'Spirits of the Fallen': 'Heroische Seele',
        'Star Form': 'Stern Form',
        'Streak Lightning': 'Blitzladung',
        'Thricecull': 'Dreifachlanze',
        'Thundercall': 'Donnerruf',
        'Turbulent Aether': 'Äthersturm',
        'Ultimate Zantetsuken': 'Goku-Zantetsuken',
        'Whirling Zantetsuken': 'Sen-Zantetsuken',
        'Wyvern Explosion': 'Wyvern Explosion',
      },
    },
    {
      'locale': 'fr',
      'replaceSync': {
        'Absolute Virtue': 'Vertu absolue',
        'Arsenal Centaur': 'Centaure de l\'Arsenal',
        'Art': 'Art',
        'Ball Lightning': 'Orbe de foudre',
        'Arsenal Urolith': 'Urolithe de l\'Arsenal',
        'Ivory Palm': 'Paume d\'ivoire',
        'Munderg, turn blood to ice': 'C\'est bien, Munderg... Glace le sang de mes ennemis',
        'Munderg, turn flesh to ash': 'Oui... Munderg, sens le feu embraser nos âmes',
        'Orlasrach': 'Orlasrach',
        'Owain': 'Owain',
        '(?<! )Ozma(?!\\w)': 'Ozma',
        'Ozmashade': 'Ombre d\'Ozma',
        'Proto Ozma(?! containment)': 'Proto-Ozma',
        'Raiden': 'Raiden',
        'Relative Virtue(?! gains)': 'Vertu relative',
        'Relative Virtue gains the effect of .*?Astral Essence.*?':
          'Vertu relative bénéficie de l\'effet.*?Bras de Lumière',
        'Relative Virtue gains the effect of .*?Umbral Essence.*?':
          'Vertu relative bénéficie de l\'effet.*?Bras de Ténèbres',
        'Shadow': 'Ombre de Proto-Ozma',
        'Streak Lightning': 'Éclair chargeant',
        'The Lance of Virtue Containment Unit':
          'l\'enceinte de confinement de la lance de la vertu',
        'The memories of heroes past live on again':
          'L\'égide des héros vaillants vous a ressuscité',
        'The Proto Ozma Containment Unit': 'l\'enceinte de confinement de Proto-Ozma',
        'The Shin-Zantetsuken Containment Unit': 'l\'enceinte de confinement de Shin-Zantetsuken',
      },
      'replaceText': {
        '\\?': ' ?',
        'Acallam Na Senorach': 'Spirale sépulcrale',
        'Acceleration Bomb': 'Bombe accélératrice',
        'Adds': 'Adds',
        'Ame-no-Sakahoko': 'Ama-no-sakahoko',
        'Astral Essence': 'Bras de Lumière',
        'Auroral Wind': 'Vent d\'aurore',
        'Bitter Barbs': 'Ronce du péché',
        'Black Hole': 'Trou noir',
        'Bleed Attack': 'Attaque saignement',
        'Booming Lament': 'Tonnerre du regret',
        'Call Wyvern': 'Appel de wyverne',
        'Carver/Spinner': 'Taillade/Spirale',
        'Cloud to Ground': 'Attaque fulminante',
        'Cube Form': 'Forme cube',
        'Eidos': 'Sarva',
        'Elemental Magicks': 'Magie élémentaire',
        'Elemental Shift': 'Changement d\'élément',
        'Execration': 'Exécration',
        'Explosion Enrage': 'Explosion Enrage',
        'Explosive Impulse': 'Impulsion explosive',
        'Flare Star': 'Astre flamboyant',
        'For Honor': 'Carnage martial',
        'Holy': 'Miracle',
        'Hostile Aspect': 'Onde polarisée',
        'Impact Stream': 'Courant d\'impact',
        'Ivory Palm': 'Paume d\'ivoire',
        'Lancing Bolt': 'Lance fulminante',
        'Lateral Zantetsuken': 'Hen Zantetsuken',
        'Legendary Geas': 'Tuerie spectrale',
        'Legendary Imbas': 'Fiel spectral',
        'Legendcarver': 'Taillade spectrale',
        'Legendspinner': 'Spirale spectrale',
        'Levinwhorl': 'Vortex de foudre',
        'Medusa Javelin': 'Javelot de Méduse',
        'Meteor': 'Météore',
        'Mourning Star': 'Étoile du matin',
        'Mythcall': 'Invitation fantasmagorique',
        'Orb x5': 'Orbe x5',
        'Ozma Ability': 'Aptitude d\'Ozma',
        'Piercing Dark': 'Lance des ténèbres',
        'Piercing Light': 'Lance de lumière',
        'Pitfall': 'Embûche',
        'Pyramid Form': 'Forme pyramide',
        'Random Form': 'Forme aléatoire',
        'Random Shade': 'Ombre aléatoire',
        'Shade Ability': 'Aptitude d\'Ombre',
        'Shingan': 'Impact oculaire',
        'Shooting Star': 'Étoile filante',
        'Soak Attack': 'Absorbez l\'attaque',
        'Spear Copy': 'Copie de lance',
        'Spear Shade': 'Ombre de lance',
        'Sphere Form': 'Forme sphère',
        'Spiritcull': 'Salve magique',
        'Spirits of the Fallen': 'Âme héroïque',
        'Stack': 'Package',
        'Star Form': 'Forme étoile',
        'Streak Lightning': 'Éclair chargeant',
        'Thricecull': 'Triple perforation',
        'Thundercall': 'Drain fulminant',
        'Turbulent Aether': 'Turbulence éthérée',
        'Ultimate Zantetsuken': 'Goku Zantetsuken',
        'Umbral Essence': 'Bras de Ténèbres',
        'Whirling Zantetsuken': 'Sen Zantetsuken',
        'Wyvern Explosion': 'Wyverne Explosion',
      },
    },
    {
      'locale': 'ja',
      'replaceSync': {
        'Absolute Virtue': 'アブソリュートヴァーチュー',
        'Arsenal Centaur': 'アーセナル・セントール',
        'Art': 'アルト',
        'Ball Lightning': '雷球',
        'Arsenal Urolith': 'アーセナル・ウロリス',
        'Ivory Palm': '白き手',
        'Orlasrach': 'オールラスラッハ',
        'Owain': 'オーウェン',
        '(?<! )Ozma(?!\\w)': 'オズマ',
        'Ozmashade': 'オズマの影',
        'Proto Ozma(?! containment)': 'プロトオズマ',
        'Raiden': 'ライディーン',
        'Relative Virtue': 'レラティブヴァーチュー',
        'Shadow': 'プロトオズマの影',
        'Streak Lightning': 'ストリークライトニング',
        'The Lance of Virtue Containment Unit': 'ランス・オブ・ヴァーチュー封印区',
        'The Proto Ozma Containment Unit': 'プロトオズマ封印区',
        'The Shin-Zantetsuken Containment Unit': '真・斬鉄剣封印区',
        'The memories of heroes past live on again': '英傑の加護の効果が発揮され、蘇生された',
        'Munderg, turn flesh to ash': '白の妖槍「ムンジャルグ」、燃え上がれ！',
        'Munderg, turn blood to ice': '白の妖槍「ムンジャルグ」、震え凍れよ！',
      },
      'replaceText': {
        '\\?': ' ?',
        'Acallam Na Senorach': '真妖槍旋',
        'Acceleration Bomb': '加速度爆弾',
        'Adds': '雑魚',
        'Ame-no-Sakahoko': '天逆鉾',
        'Astral Essence': '光の腕',
        'Auroral Wind': 'オーロラルウィンド',
        'Bitter Barbs': '罪の荊棘',
        'Black Hole': 'ブラックホール',
        'Bleed Attack': 'ブリードアタック',
        'Booming Lament': '哀惜の雷鳴',
        'Call Wyvern': 'コールワイバーン',
        'Carver/Spinner': '妖槍振/妖槍旋',
        'Cloud to Ground': '襲雷',
        'Cube Form': '立方体',
        'Eidos': '変異',
        'Elemental Magicks': 'エレメンタルマジック',
        'Elemental Shift': 'エレメントスイッチ',
        'Execration': 'エクセクレイション',
        '(?<! )Explosion': '爆発',
        'Explosive Impulse': 'エクスプロシブインパルス',
        'Flare Star': 'フレアスター',
        'For Honor': '戦死撃',
        'Holy': 'ホーリー',
        'Hostile Aspect': '極性波動',
        'Impact Stream': 'インパクトストリーム',
        'Ivory Palm': '白き手',
        'Lancing Bolt': '雷槍',
        'Lateral Zantetsuken': '片・斬鉄剣',
        'Legendary Geas': '妖槍乱撃',
        'Legendary Imbas': '妖槍邪念',
        'Legendcarver': '妖槍振',
        'Legendspinner': '妖槍旋',
        'Levinwhorl': '渦雷',
        'Medusa Javelin': 'メデューサジャベリン',
        'Meteor': 'メテオ',
        'Mourning Star': 'モーニングスター',
        'Mythcall': '幻槍招来',
        'Orb x5': '玉 x5',
        'Ozma Ability': 'オズマ スキル',
        'Piercing Dark': '闇の槍',
        'Piercing Light': '光の槍',
        'Pitfall': '強襲',
        'Pyramid Form': '角錐形態',
        'Random Form': 'ランダム形態',
        'Random Shade': 'ランダム形',
        'Shade Ability': '幻影 スキル',
        'Shingan': '真眼撃',
        'Shooting Star': 'シューティングスター',
        'Soak Attack': 'アタック',
        'Spear Copy': '剣幻影',
        'Spear Shade': '剣形',
        'Sphere Form': '球形態',
        'Spiritcull': '連装魔',
        'Spirits of the Fallen': '英霊魂',
        'Stack': '集合',
        'Star Form': '星形',
        'Streak Lightning': 'ストリークライトニング',
        'Thricecull': '三連槍',
        'Thundercall': '招雷',
        'Turbulent Aether': 'エーテル乱流',
        'Ultimate Zantetsuken': '極・斬鉄剣',
        'Umbral Essence': '闇の腕',
        'Whirling Zantetsuken': '旋・斬鉄剣',
        'Wyvern Explosion': 'ワイバーン 爆発',
      },
    },
    {
      'locale': 'cn',
      'replaceSync': {
        'Absolute Virtue': '绝对的美德',
        'Arsenal Centaur': '兵武半人马',
        'Art': '亚特',
        'Ball Lightning': '雷球',
        'Arsenal Urolith': '兵武乌洛里石',
        'Ivory Palm': '白手',
        'Orlasrach': '烈焰金枪',
        'Owain': '欧文',
        '(?<! )Ozma(?!\\w)': '奥兹玛',
        'Ozmashade': '奥兹玛之影',
        'Proto Ozma(?! Containment)': '奥兹玛原型',
        'Raiden': '莱丁',
        'Relative Virtue(?! gains)': '相对的美德',
        'Shadow': '奥兹玛原型之影',
        'Streak Lightning': '强袭雷光',
        '5 minutes have elapsed since your last activity.': '已经5分钟没有进行任何操作',
        'The memories of heroes past live on again': '发动了英杰的加护的效果，重新苏醒了过来',
        'Munderg, turn flesh to ash': '红颈妖枪，点燃一切！',
        'Munderg, turn blood to ice': '红颈妖枪，冻结万物！',
        'The Lance of Virtue Containment Unit': '美德之枪封印区',
        'The Shin-Zantetsuken Containment Unit': '真·斩铁剑封印区',
        'The Proto Ozma Containment Unit': '奥兹玛原型封印区',
        'Relative Virtue gains the effect of .*?Astral Essence.*?': '相对的美德附加了“光之腕”效果',
        'Relative Virtue gains the effect of .*?Umbral Essence.*?': '相对的美德附加了“暗之腕”效果',
      },
      'replaceText': {
        'Acallam Na Senorach': '真妖枪旋',
        'Acceleration Bomb': '加速度炸弹',
        'Adds': '小怪',
        'Ame-no-Sakahoko': '天逆矛',
        'Astral Essence': '光之腕',
        'Auroral Wind': '极光之风',
        'Bitter Barbs': '罪恶荆棘',
        'Black Hole': '黑洞',
        'Bleed Attack': '流血攻击',
        'Booming Lament': '哀痛雷鸣',
        'Call Wyvern': '召唤飞龙',
        'Carver/Spinner': '靠近/远离',
        'Cloud to Ground': '袭雷',
        'Cube Form': '立方体形式',
        'Eidos': '变异',
        'Elemental Magicks': '元素魔法',
        'Elemental Shift': '元素开关',
        'Execration': '缩小射线',
        'Explosive Impulse': '爆炸性冲击',
        'Flare Star': '耀星',
        'For Honor': '战死击',
        'Holy': '神圣',
        'Hostile Aspect': '极性波动',
        'Impact Stream': '冲击流',
        'Ivory Palm': '白手',
        'Lancing Bolt': '雷枪',
        'Lateral Zantetsuken': '片·斩铁剑',
        'Legendary Geas': '妖枪乱击',
        'Legendary Imbas': '妖枪邪念',
        'Legendcarver': '妖枪振',
        'Legendspinner': '妖枪振',
        'Levinwhorl': '涡雷',
        'Medusa Javelin': '美杜莎投枪',
        'Meteor': '陨石',
        'Mourning Star': '启明星',
        'Mythcall': '幻枪招来',
        'Orb x5': '球 x5',
        'Ozma Ability': '奥兹玛技能',
        'Piercing Dark': '暗之枪',
        'Piercing Light': '光之枪',
        'Pitfall': '强袭',
        'Pyramid Form': '金字塔形式',
        'Random Form': '随机形式',
        'Random Shade': '随机影子',
        'Shade Ability': '影子技能',
        'Shingan': '真眼击',
        'Shooting Star': '流星',
        'Soak Attack': '被攻击',
        'Spear Copy': '矛复制',
        'Spear Shade': '矛影子',
        'Sphere Form': '球形式',
        'Spiritcull': '连装魔',
        'Spirits of the Fallen': '英灵魂',
        'Stack': '分摊',
        'Star Form': '星星形式',
        'Streak Lightning': '强袭雷光',
        'Thricecull': '三连枪',
        'Thundercall': '招雷',
        'Turbulent Aether': '以太乱流',
        'Ultimate Zantetsuken': '极·斩铁剑',
        'Umbral Essence': '暗之腕',
        'Whirling Zantetsuken': '旋·斩铁剑',
        'Wyvern Explosion': '飞龙爆炸',
      },
    },
    {
      'locale': 'tc',
      'replaceSync': {
        'Absolute Virtue': '絕對的美德',
        'Arsenal Centaur': '兵武半人馬',
        'Art': '亞特',
        'Ball Lightning': '雷球',
        'Arsenal Urolith': '兵武烏洛里石',
        'Ivory Palm': '白手',
        'Orlasrach': '黑焰魔槍',
        'Owain': '歐文',
        '(?<! )Ozma(?!\\w)': '奧斯瑪',
        'Ozmashade': '奧斯瑪之影',
        'Proto Ozma(?! Containment)': '奧斯瑪原型',
        'Raiden': '萊丁',
        'Relative Virtue(?! gains)': '相對的美德',
        'Shadow': '奧斯瑪原型之影',
        'Streak Lightning': '強襲雷光',
        '5 minutes have elapsed since your last activity.': '已經5分鐘沒有進行任何操作',
        'The memories of heroes past live on again': '發動了重生的效果，重新蘇醒了過來！',
        'Munderg, turn flesh to ash': '白焰妖槍，點燃一切！',
        'Munderg, turn blood to ice': '白焰妖槍，凍結萬物！',
        'The Lance of Virtue Containment Unit': '美德之槍封印區',
        'The Shin-Zantetsuken Containment Unit': '真·斬鐵劍封印區',
        'The Proto Ozma Containment Unit': '奧斯瑪原型封印區',
        'Relative Virtue gains the effect of .*?Astral Essence.*?': '相對的美德附加了“光之腕”效果',
        'Relative Virtue gains the effect of .*?Umbral Essence.*?': '相對的美德附加了“暗之腕”效果',
      },
      'replaceText': {
        'Acallam Na Senorach': '真妖槍旋',
        'Acceleration Bomb': '加速度炸彈',
        'Adds': '小怪',
        'Ame-no-Sakahoko': '天逆矛',
        'Astral Essence': '光之腕',
        'Auroral Wind': '極光之風',
        'Bitter Barbs': '罪惡荊棘',
        'Black Hole': '黑洞',
        'Bleed Attack': '攻擊',
        'Booming Lament': '哀痛雷鳴',
        'Call Wyvern': '召喚飛龍',
        'Carver/Spinner': '靠近/遠離',
        'Cloud to Ground': '襲雷',
        'Cube Form': '立方體形態',
        'Eidos': '變異',
        'Elemental Magicks': '元素魔法',
        'Elemental Shift': '元素開關',
        'Execration': '縮小射線',
        'Explosive Impulse': '爆炸性衝擊',
        'Flare Star': '耀星',
        'For Honor': '戰死擊',
        'Holy': '神聖',
        'Hostile Aspect': '極性波動',
        'Impact Stream': '衝擊流',
        'Ivory Palm': '白手',
        'Lancing Bolt': '雷槍',
        'Lateral Zantetsuken': '片·斬鐵劍',
        'Legendary Geas': '妖槍亂擊',
        'Legendary Imbas': '妖槍邪念',
        'Legendcarver': '妖槍振',
        'Legendspinner': '妖槍振',
        'Levinwhorl': '渦雷',
        'Medusa Javelin': '美杜莎投槍',
        'Meteor': '隕石',
        'Mourning Star': '晨星鎚',
        'Mythcall': '幻槍招來',
        'Orb x5': '重力地雷',
        'Ozma Ability': '奧斯瑪技能',
        'Piercing Dark': '暗之槍',
        'Piercing Light': '光之槍',
        'Pitfall': '強襲',
        'Pyramid Form': '金字塔形態',
        'Random Form': '隨機形態',
        'Random Shade': '隨機影子',
        'Shade Ability': '影子技能',
        'Shingan': '真眼擊',
        'Shooting Star': '流星',
        'Soak Attack': '攻擊',
        'Spear Copy': '幻槍振',
        'Spear Shade': '矛影子',
        'Sphere Form': '球形態',
        'Spiritcull': '連裝魔',
        'Spirits of the Fallen': '英靈魂',
        'Stack': '分攤',
        'Star Form': '星星形態',
        'Streak Lightning': '雷槍擊',
        'Thricecull': '三連槍',
        'Thundercall': '招雷',
        'Turbulent Aether': '乙太亂流',
        'Ultimate Zantetsuken': '極·斬鐵劍',
        'Umbral Essence': '暗之腕',
        'Whirling Zantetsuken': '旋·斬鐵劍',
        'Wyvern Explosion': '飛龍爆炸',
      },
    },
    {
      'locale': 'ko',
      'replaceSync': {
        'Absolute Virtue': '절대미덕',
        'Arsenal Centaur': '무기고 켄타우로스',
        'Art': '아르트',
        'Ball Lightning': '전기 구체',
        'Arsenal Urolith': '무기고 요결석',
        'Ivory Palm': '하얀 손',
        'Orlasrach': '오를라스라흐',
        'Owain': '오와인',
        '(?<! )Ozma(?!\\w)': '오즈마',
        'Ozmashade': '오즈마의 그림자',
        'Proto Ozma(?! Containment)': '프로토 오즈마',
        'Raiden': '라이딘',
        'Relative Virtue': '상대미덕',
        'Shadow': '프로토 오즈마의 그림자',
        'Streak Lightning': '연쇄 번개',
        'Munderg, turn flesh to ash': '하얀 요창 \'문데르크\'여, 불타올라라!',
        'Munderg, turn blood to ice': '하얀 요창 \'문데르크\'여, 얼어붙어라!',
        'The Lance of Virtue Containment Unit': '미덕의 창 봉인 구역',
        'The Shin-Zantetsuken Containment Unit': '진 참철검 봉인 구역',
        'The Proto Ozma Containment Unit': '프로토 오즈마 봉인 구역',
        'The memories of heroes past live on again': '영걸의 가호의 효과가 발휘되어 부활했습니다',
      },
      'replaceText': {
        'Acallam Na Senorach': '피어너의 창',
        'Acceleration Bomb': '가속도 폭탄',
        'Adds': '쫄',
        'Ame-no-Sakahoko': '아메노사카호코',
        'Astral Essence': '빛의 팔',
        'Auroral Wind': '오로라 바람',
        'Bitter Barbs': '죄의 가시',
        'Black Hole': '블랙홀',
        'Bleed Attack': '출혈 공격',
        'Booming Lament': '애도의 뇌명',
        'Call Wyvern': '와이번 소환',
        'Carver/Spinner': '요창 떨치기/후리기',
        'Cloud to Ground': '습뢰',
        'Cube Form': '네모 모양',
        'Eidos': '변이',
        'Elemental Magicks': '엘리멘탈 마법',
        'Elemental Shift': '엘리멘탈 변환',
        'Execration': '혐오의 저주',
        '(?<! )Explosion': '폭발',
        'Explosive Impulse': '폭발적 추진력',
        'Flare Star': '타오르는 별',
        'For Honor': '전사격',
        'Holy': '홀리',
        'Hostile Aspect': '극성 파동',
        'Impact Stream': '충격 기류',
        'Ivory Palm': '하얀 손',
        'Lancing Bolt': '뇌창',
        'Lateral Zantetsuken': '편 참철검',
        'Legendary Geas': '요창난격',
        'Legendary Imbas': '요창의 사념',
        'Legendcarver': '요창 떨치기',
        'Legendspinner': '요창 후리기',
        'Levinwhorl': '와뢰',
        'Medusa Javelin': '메두사의 투창',
        'Meteor': '메테오',
        'Mourning Star': '샛별',
        'Mythcall': '환창 소환',
        'Orb x5': '구슬 5회',
        'Ozma Ability': '오즈마 능력',
        'Piercing Dark': '어둠의 창',
        'Piercing Light': '빛의 창',
        'Pitfall': '강습',
        'Pyramid Form': '세모 모양',
        'Random Form': '무작위 모양',
        'Random Shade': '무작위 그림자',
        'Shade Ability': '그림자 능력',
        'Shingan': '진안격',
        'Shooting Star': '유성',
        'Soak Attack': '평타 쉐어 공격',
        'Spear Copy': '창 복사',
        'Spear Shade': '창 그림자',
        'Sphere Form': '원 모양',
        'Spiritcull': '마법 연발',
        'Spirits of the Fallen': '영령혼',
        'Stack': '모이기',
        'Star Form': '별 모양',
        'Streak Lightning': '연쇄 번개',
        'Thricecull': '삼연창',
        'Thundercall': '초뢰',
        'Turbulent Aether': '에테르 난류',
        'Ultimate Zantetsuken': '극 참철검',
        'Umbral Essence': '어둠의 팔',
        'Whirling Zantetsuken': '선 참철검',
        'Wyvern Explosion': '와이번 폭발',
      },
    },
  ],
};

export default triggerSet;
